import { SortingState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import TableContainer from "Common/TableContainer";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import {
  Fragment,
  useEffect,
  useRef,
  useState
} from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  fetchLatestPermissions
} from "slices/auth/login/thunk";
import {
  fetchTag as OnfetchTags
} from "slices/tag/thunk";
const TagList = () => {
  document.title = "Category List";
  const dispatch = useDispatch<any>();

  // Redux selector for category state
  const selectTags = createSelector(
    (state: any) => state.tags,
    (tags) => ({
      categories: tags.tagList,
      rowCount: tags.rowCount,
      loading: tags.loading,
      error: tags.error,
    })
  );

  const { categories, rowCount, loading, error } = useSelector(selectTags);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.master?.app_name
  );
  const tag_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.master?.modules?.tag
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(rowCount);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  // State for edit functionality
  const [editCategory, setEditCategory] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const navigate = useNavigate();
  useEffect(() => {
    dispatch(
      OnfetchTags({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, sortField, sortDirection]);

  const prevFilters = useRef(filterValue);

  useEffect(() => {
    if (JSON.stringify(prevFilters.current) !== JSON.stringify(filterValue)) {
      prevFilters.current = filterValue;
      dispatch(
        OnfetchTags({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    }
  }, [filterValue]);

  useEffect(() => {
    setData(categories);
  }, [categories]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page: newPage.toString() });
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  // Handle edit action
  const handleEdit = (item: any) => {
    setEditCategory(!editCategory);
    setEdit({
      id: item.id,
      categoryName: item.category_name,
      state: item.state_name,
      status: item.is_active,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createDate: item.created_at,
      updatedDate: item.updated_at,
    });
  };
  const handleStatusToggle = async (tag: any) => {
    try {
      const updatedStatus = !tag.status;
      const payload = { status: updatedStatus };

      setIsStatusUpdateLoading(true);

      // Use the correct key (id or tag_id)
      await Axios.patch(
        `api/tags/edit-status/${tag.id || tag.tag_id}/`,
        payload
      );

      toast.success(
        `Tag ${updatedStatus ? "activated" : "deactivated"} successfully`
      );

      // Refresh list
      dispatch(
        OnfetchTags({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const DynamicAction =
    tag_permissions?.permissions?.Update === true
      ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => (
            <ul className="d-flex gap-2 list-unstyled mb-0">
              <li>
                <Link
                  to={`/edit-tag/${row.original.tag_id}`}
                  state={{ tag: row.original }}
                  className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
                  onClick={() => handleEdit(row.original)}
                // onClick={async (e) => {
                //             e.preventDefault();
                //             await handleEdit("Update", row.original);
                //         }}
                >
                  <i className="ph-pencil"></i>
                </Link>
              </li>
            </ul>
          ),
        },
      ]
      : [];

  const columns =
    // useMemo(
    //   () =>
    [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "Tags",
        accessorKey: "tag_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/tags/column-filter-dropdown/?column_filter=tag_name",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const pkg = row.original;
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(pkg)}
            >
              <i
                className={`bi ${pkg.status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                  }`}
                style={{ color: "#0073BB" }}
              />
            </div>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      ...DynamicAction,

      // ...(category_permissions?.Update ? [


      // ] : []),
    ];
  //   [dispatch, categories]
  // );

  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Tags</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {tag_permissions?.permissions?.Create === true &&
                          <Link
                            to="/add-tag"
                            className="btn"
                            style={{
                              backgroundColor: "#0073BB",
                              borderColor: "#0073BB",
                              color: "#fff",
                            }}
                          >
                            <i className="bi bi-plus-circle align-baseline me-1"></i>{" "}
                            Add Tags
                          </Link>
                        }
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search categories..."
                      manualSorting={true}
                      colValue={setFilterValue}
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default TagList;
